import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, UserCheck, KeyRound, Copy, Check, Eye, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { DeleteButton } from "@/components/DeleteButton";
import { SCHOOL_USER_TITLES } from "@/lib/constants";
import { Checkbox } from "@/components/ui/checkbox";
import { StatesPicker } from "@/components/StatesPicker";

const CC_ROLE_SET = ["Campus Coordinator", "Campus Coordinator Supervisor"];
const STATE_ROLES = ["Campus Coordinator", "Campus Coordinator Supervisor", "Area Manager"];

function FacilityPicker({ value, onChange, facilities, testId }) {
  const selected = value || [];
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const list = facilities
    .filter((f) => !term || [f.name, f.city, f.state, f.facility_code].filter(Boolean).join(" ").toLowerCase().includes(term))
    .slice(0, 60);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="border rounded-md p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-zinc-500">{selected.length ? `${selected.length} site${selected.length > 1 ? "s" : ""} selected` : "No specific sites — their AM email and states will be used"}</p>
        {selected.length > 0 && <button className="text-xs text-cc-blue hover:underline" onClick={() => onChange([])} data-testid={`${testId}-clear`}>Clear all</button>}
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search facilities…" className="h-8 text-xs mb-2" data-testid={`${testId}-search`} />
      <div className="max-h-52 overflow-y-auto cc-scroll space-y-1">
        {list.map((f) => (
          <label key={f.id} className={`flex items-center gap-2 text-xs px-1.5 py-1 rounded cursor-pointer ${selected.includes(f.id) ? "bg-cc-blue/10 text-cc-blue" : "hover:bg-zinc-50 text-zinc-600"}`}>
            <Checkbox checked={selected.includes(f.id)} onCheckedChange={() => toggle(f.id)} data-testid={`${testId}-${f.id}`} />
            <span className="truncate">{f.name}{f.state ? ` · ${f.state}` : ""}</span>
          </label>
        ))}
        {list.length === 0 && <p className="text-xs text-zinc-400 py-2">No facilities match.</p>}
      </div>
    </div>
  );
}

const ROLES = ["Campus Coordinator", "Campus Coordinator Supervisor", "School User", "Student", "DOR", "Clinical Instructor", "Recruiter", "Area Manager"];
const DISCIPLINES = ["PT", "PTA", "OT", "OTA", "SLP", "Other"];
const FILTER_ROLES = ["All", ...ROLES, "Super Admin", "Guest"];

function PendingGuestRow({ g, onAssign }) {
  const [role, setRole] = useState("Campus Coordinator");
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-3 border rounded-md p-3" data-testid={`guest-${g.id}`}>
      <div className="flex-1 min-w-[180px]">
        <p className="font-medium text-zinc-800 text-sm">{g.name || "(no name)"}</p>
        <p className="text-xs text-zinc-400">{g.email}{g.auth_type === "sso" ? " · signed in via SSO" : ""}</p>
      </div>
      <Select value={role} onValueChange={setRole}>
        <SelectTrigger className="w-48" data-testid={`guest-role-${g.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" className="bg-cc-green text-white" disabled={busy} data-testid={`assign-guest-${g.id}`}
        onClick={async () => { setBusy(true); await onAssign(g, role); setBusy(false); }}>
        <UserCheck className="w-4 h-4 mr-1" />Assign role
      </Button>
    </div>
  );
}

export default function Users() {
  const { user: currentUser, impersonate } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = currentUser.role === "Super Admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ role: "Campus Coordinator" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [schools, setSchools] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [students, setStudents] = useState([]);
  const [roleFilter, setRoleFilter] = useState("All");
  const load = () => api.get("/users").then(({ data }) => setRows(data));
  const loadStudents = () => api.get("/students").then(({ data }) => setStudents(data)).catch(() => {});
  useEffect(() => {
    load();
    loadStudents();
    api.get("/schools").then(({ data }) => setSchools(data)).catch(() => {});
    api.get("/facilities").then(({ data }) => setFacilities(data)).catch(() => {});
  }, []);

  const orgOptions = (role) => role === "School User" || role === "Student" ? schools : role === "Clinical Instructor" || role === "DOR" ? facilities : null;
  const orgTypeFor = (role) => role === "School User" || role === "Student" ? "school" : role === "Clinical Instructor" || role === "DOR" ? "facility" : null;
  const orgLabel = (role) => role === "Clinical Instructor" || role === "DOR" ? "Facility" : "School";
  const studentFor = (userId) => students.find((s) => s.user_id === userId);

  const [resetting, setResetting] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetResult, setResetResult] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const openReset = (u) => { setResetting(u); setResetPw(""); setResetResult(null); setCopied(false); };

  const resendInvite = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/resend-invite`);
      toast.success(data.mode === "sso" ? `Reminder sent — ${u.name} signs in with Microsoft` : `Activation link re-sent to ${u.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doReset = async () => {
    setResetBusy(true);
    try {
      const payload = resetPw.trim() ? { password: resetPw.trim() } : {};
      const { data } = await api.post(`/users/${resetting.id}/reset-password`, payload);
      setResetResult(data);
      toast.success("Password reset");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResetBusy(false); }
  };
  const copyPw = () => { navigator.clipboard?.writeText(resetResult.password); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const doImpersonate = async (u) => {
    try { await impersonate(u.id); navigate("/dashboard"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [dates, setDates] = useState({ gradFrom: "", gradTo: "", rsFrom: "", rsTo: "", reFrom: "", reTo: "" });
  const [stMap, setStMap] = useState({}); // user_id -> {rotation_start, rotation_end, graduation_date}
  useEffect(() => { api.get("/students-touchpoints").then(({ data }) => {
    const m = {}; data.forEach((s) => { if (s.user_id) m[s.user_id] = s; }); setStMap(m);
  }).catch(() => {}); }, []);
  const dateActive = Object.values(dates).some(Boolean);
  const inR = (v, a, b) => { if (!v) return false; const d = new Date(v); if (a && d < new Date(a)) return false; if (b && d > new Date(b)) return false; return true; };
  const passDates = (u) => {
    if (!dateActive) return true;
    const s = stMap[u.id]; if (!s) return false;
    if ((dates.gradFrom || dates.gradTo) && !inR(s.graduation_date, dates.gradFrom, dates.gradTo)) return false;
    if ((dates.rsFrom || dates.rsTo) && !inR(s.rotation_start, dates.rsFrom, dates.rsTo)) return false;
    if ((dates.reFrom || dates.reTo) && !inR(s.rotation_end, dates.reFrom, dates.reTo)) return false;
    return true;
  };

  const guests = rows.filter((u) => (u.role || "Guest") === "Guest");
  const others = rows.filter((u) => (u.role || "Guest") !== "Guest")
    .filter((u) => roleFilter === "All" || (u.role || "Guest") === roleFilter)
    .filter(passDates);

  const assignGuest = async (g, role) => {
    try { await api.patch(`/users/${g.id}`, { role }); toast.success(`${g.name || g.email} is now a ${role}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not assign role"); }
  };

  const saveEdit = async () => {
    try {
      const payload = { name: editForm.name, role: editForm.role };
      if (isSuperAdmin && editForm.email && editForm.email !== editing.email) payload.email = editForm.email;
      payload.organization_type = orgTypeFor(editForm.role);
      payload.organization_id = orgOptions(editForm.role) ? (editForm.organization_id || null) : null;
      if (editForm.role === "Student") {
        payload.discipline = editForm.discipline;
        payload.graduation_date = editForm.graduation_date || null;
        payload.employment_interest = editForm.employment_interest;
      }
      if (editForm.role === "School User") payload.title = editForm.title || null;
      if (CC_ROLE_SET.includes(editForm.role)) payload.managed_states = editForm.managed_states || [];
      if (editForm.role === "Area Manager") {
        payload.managed_states = editForm.managed_states || [];
        payload.managed_facility_ids = editForm.managed_facility_ids || [];
      }
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("User updated"); setEditing(null); load(); loadStudents();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const invite = async () => {
    try {
      const payload = { ...form, organization_type: orgTypeFor(form.role), organization_id: orgOptions(form.role) ? form.organization_id : null };
      const { data } = await api.post("/users", payload); toast.success(data.sso ? "SSO user invited — they'll sign in with Microsoft" : "User invited"); if (data.verification_link) toast.info("Dev verify link sent"); setOpen(false); setForm({ role: "Campus Coordinator" }); load(); loadStudents();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (u, status) => { await api.patch(`/users/${u.id}/status`, { account_status: status }); load(); };

  return (
    <div>
      <PageHeader title="Users" subtitle="Accounts, roles & onboarding">
        <Button onClick={() => setOpen(true)} className="bg-cc-blue text-white" data-testid="invite-user-btn">+ Invite User</Button>
      </PageHeader>

      {guests.length > 0 && (
        <Card title={`Pending Guests (${guests.length})`} className="mb-6 border-cc-orange/40" data-testid="pending-guests-card">
          <p className="text-sm text-zinc-500 mb-3">These people have signed in but don't have a role yet. Assign a role to give them access.</p>
          <div className="space-y-2">
            {guests.map((g) => <PendingGuestRow key={g.id} g={g} onAssign={assignGuest} />)}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <p className="text-sm text-zinc-500">{others.length} {others.length === 1 ? "account" : "accounts"}{roleFilter !== "All" ? ` · ${roleFilter}` : ""}</p>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Filter by role</span>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-48" data-testid="role-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{FILTER_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-4 mb-4 border-t pt-3" data-testid="user-date-filters">
          {[["Grad date", "gradFrom", "gradTo"], ["Rotation start", "rsFrom", "rsTo"], ["Rotation end", "reFrom", "reTo"]].map(([lbl, a, b]) => (
            <div key={a}>
              <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-semibold mb-1">{lbl}</p>
              <div className="flex items-center gap-1">
                <Input type="date" className="h-8 w-36" value={dates[a]} onChange={(e) => setDates({ ...dates, [a]: e.target.value })} data-testid={`filter-${a}`} />
                <span className="text-zinc-400">→</span>
                <Input type="date" className="h-8 w-36" value={dates[b]} onChange={(e) => setDates({ ...dates, [b]: e.target.value })} data-testid={`filter-${b}`} />
              </div>
            </div>
          ))}
          {dateActive && <Button variant="ghost" size="sm" className="h-8 text-xs text-zinc-500" onClick={() => setDates({ gradFrom: "", gradTo: "", rsFrom: "", rsTo: "", reFrom: "", reTo: "" })} data-testid="filter-dates-clear">Clear dates</Button>}
        </div>
        {others.length === 0 ? <EmptyState text="No users." /> : (
          <div className="overflow-x-auto cc-scroll -m-5">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                <tr><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Email</th><th className="text-left px-5 py-3">Role</th><th className="text-left px-5 py-3">Status</th><th className="px-5 py-3"></th></tr>
              </thead>
              <tbody>
                {others.map((u) => (
                  <tr key={u.id} className="border-t" data-testid={`user-${u.id}`}>
                    <td className="px-5 py-3 font-medium text-zinc-800">
                      {u.role === "Student" && studentFor(u.id)
                        ? <button onClick={() => navigate(`/students/${studentFor(u.id).id}`)} data-testid={`view-student-${u.id}`} className="text-cc-blue hover:underline">{u.name}</button>
                        : u.name}
                    </td>
                    <td className="px-5 py-3 text-zinc-600">{u.email}</td>
                    <td className="px-5 py-3 text-zinc-600">{u.role}</td>
                    <td className="px-5 py-3"><StatusBadge status={u.account_status === "Active" ? "Active" : u.account_status} /></td>
                    <td className="px-5 py-3 text-right">
                      {u.role !== "Super Admin" && <button onClick={() => { const s = studentFor(u.id); setEditing(u); setEditForm({ name: u.name, role: u.role, email: u.email, organization_id: u.organization_id, title: u.title, managed_states: u.managed_states || [], managed_facility_ids: u.managed_facility_ids || [], discipline: s?.discipline, graduation_date: s?.graduation_date, employment_interest: s?.employment_interest }); }} data-testid={`edit-user-${u.id}`} className="text-zinc-400 hover:text-cc-blue mr-3 align-middle"><Pencil className="w-4 h-4 inline" /></button>}
                      {u.role !== "Super Admin" && u.auth_type !== "sso" && <button onClick={() => openReset(u)} data-testid={`reset-pw-${u.id}`} title="Reset password" className="text-zinc-400 hover:text-cc-blue mr-3 align-middle"><KeyRound className="w-4 h-4 inline" /></button>}
                      {isSuperAdmin && u.role !== "Super Admin" && <button onClick={() => doImpersonate(u)} data-testid={`impersonate-${u.id}`} title="Impersonate this user" className="text-zinc-400 hover:text-cc-blue mr-3 align-middle"><Eye className="w-4 h-4 inline" /></button>}
                      {isSuperAdmin && u.role !== "Super Admin" && u.id !== currentUser.id && <DeleteButton endpoint={`/users/${u.id}`} label="user" name={u.name} onDeleted={load} testId={`delete-user-${u.id}`} className="mr-3" />}
                      {(u.account_status === "Invited — SSO" || u.account_status === "Pending Email Verification" || u.account_status === "Pending Activation") && <button onClick={() => resendInvite(u)} data-testid={`resend-invite-${u.id}`} title="Resend invite" className="text-zinc-400 hover:text-cc-blue mr-3 align-middle"><Send className="w-4 h-4 inline" /></button>}
                      {u.account_status !== "Active" && <Button size="sm" variant="outline" onClick={() => setStatus(u, "Active")} data-testid={`activate-${u.id}`}>Activate</Button>}
                      {u.account_status === "Active" && u.role !== "Super Admin" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setStatus(u, "Suspended")} data-testid={`suspend-${u.id}`}>Suspend</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invite User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Name"><Input data-testid="user-name" onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Email"><Input data-testid="user-email" onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            {(form.email || "").toLowerCase().endsWith("@therapymgmt.com") && (
              <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-700" data-testid="sso-invite-hint">
                <span className="font-semibold">Microsoft SSO account.</span> No password will be set — they'll sign in with their therapymgmt.com Microsoft account and get this role on first login.
              </div>
            )}
            <Field label="Role">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, organization_id: undefined, title: undefined })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {orgOptions(form.role) && (
              <Field label={orgLabel(form.role)}>
                <Select value={form.organization_id || ""} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                  <SelectTrigger data-testid="user-org"><SelectValue placeholder={`Select a ${orgLabel(form.role).toLowerCase()}`} /></SelectTrigger>
                  <SelectContent>{orgOptions(form.role).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            {form.role === "School User" && (
              <Field label="Title">
                <Select value={form.title || ""} onValueChange={(v) => setForm({ ...form, title: v })}>
                  <SelectTrigger data-testid="user-title"><SelectValue placeholder="Select a title" /></SelectTrigger>
                  <SelectContent>{SCHOOL_USER_TITLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            {STATE_ROLES.includes(form.role) && (
              <Field label={form.role === "Area Manager" ? "States they cover (optional)" : "States they are responsible for"}>
                <StatesPicker value={form.managed_states} onChange={(v) => setForm({ ...form, managed_states: v })} testId="user-states" />
              </Field>
            )}
            {form.role === "Area Manager" && (
              <Field label="Their sites (optional — adds to any matched by their AM email)">
                <FacilityPicker value={form.managed_facility_ids} onChange={(v) => setForm({ ...form, managed_facility_ids: v })} facilities={facilities} testId="user-facilities" />
              </Field>
            )}
            {form.role === "Student" && (
              <Field label="Discipline">
                <Select value={form.discipline || ""} onValueChange={(v) => setForm({ ...form, discipline: v })}>
                  <SelectTrigger data-testid="user-discipline"><SelectValue placeholder="Select a discipline" /></SelectTrigger>
                  <SelectContent>{DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
          </div>
          <DialogFooter><Button onClick={invite} className="bg-cc-blue text-white" data-testid="send-invite-btn">Send Invite</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Name"><Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" /></Field>
            {isSuperAdmin && (
              <Field label="Email"><Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email" /></Field>
            )}
            <Field label="Role">
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v, organization_id: undefined, title: undefined })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {orgOptions(editForm.role) && (
              <Field label={orgLabel(editForm.role)}>
                <Select value={editForm.organization_id || ""} onValueChange={(v) => setEditForm({ ...editForm, organization_id: v })}>
                  <SelectTrigger data-testid="edit-user-org"><SelectValue placeholder={`Select a ${orgLabel(editForm.role).toLowerCase()}`} /></SelectTrigger>
                  <SelectContent>{orgOptions(editForm.role).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            {editForm.role === "School User" && (
              <Field label="Title">
                <Select value={editForm.title || ""} onValueChange={(v) => setEditForm({ ...editForm, title: v })}>
                  <SelectTrigger data-testid="edit-user-title"><SelectValue placeholder="Select a title" /></SelectTrigger>
                  <SelectContent>{SCHOOL_USER_TITLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            {CC_ROLE_SET.includes(editForm.role) && (
              <Field label="States they are responsible for">
                <p className="text-xs text-zinc-400 mb-2">Schools and students in these states are linked to this coordinator automatically (a coordinator assigned directly on a school always wins).</p>
                <StatesPicker value={editForm.managed_states} onChange={(v) => setEditForm({ ...editForm, managed_states: v })} testId="edit-user-states" />
              </Field>
            )}
            {editForm.role === "Area Manager" && (
              <>
                <Field label="States they cover (optional)">
                  <p className="text-xs text-zinc-400 mb-2">Every active facility in these states is included in their reports.</p>
                  <StatesPicker value={editForm.managed_states} onChange={(v) => setEditForm({ ...editForm, managed_states: v })} testId="edit-user-states" />
                </Field>
                <Field label="Their sites">
                  <p className="text-xs text-zinc-400 mb-2">Facilities where they are already the Area Manager (AM email from the SharePoint sync) are included automatically — pick sites here to add or override.</p>
                  <FacilityPicker value={editForm.managed_facility_ids} onChange={(v) => setEditForm({ ...editForm, managed_facility_ids: v })} facilities={facilities} testId="edit-user-facilities" />
                </Field>
              </>
            )}
            {editForm.role === "Student" && (
              <div className="border-t pt-4 space-y-4" data-testid="student-profile-section">
                <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Student profile</p>
                <Field label="Discipline">
                  <Select value={editForm.discipline || ""} onValueChange={(v) => setEditForm({ ...editForm, discipline: v })}>
                    <SelectTrigger data-testid="edit-user-discipline"><SelectValue placeholder="Select a discipline" /></SelectTrigger>
                    <SelectContent>{DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Graduation date">
                  <Input type="date" value={editForm.graduation_date ? editForm.graduation_date.slice(0, 10) : ""} onChange={(e) => setEditForm({ ...editForm, graduation_date: e.target.value })} data-testid="edit-user-graduation" />
                </Field>
                <Field label="Employment interest">
                  <Select value={String(editForm.employment_interest ?? "")} onValueChange={(v) => setEditForm({ ...editForm, employment_interest: v === "true" })}>
                    <SelectTrigger data-testid="edit-user-employment"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="true">Interested</SelectItem><SelectItem value="false">Not interested</SelectItem></SelectContent>
                  </Select>
                </Field>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={saveEdit} className="bg-cc-blue text-white" data-testid="save-user-btn">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reset password</DialogTitle></DialogHeader>
          {resetting && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-600">Reset the password for <span className="font-medium text-zinc-800">{resetting.name}</span> <span className="text-zinc-400">({resetting.email})</span>.</p>
              {!resetResult ? (
                <>
                  <Field label="New password (leave blank to auto-generate)">
                    <Input data-testid="reset-pw-input" type="text" value={resetPw} placeholder="Auto-generate a secure password" onChange={(e) => setResetPw(e.target.value)} />
                  </Field>
                  <p className="text-xs text-zinc-400">If you type a password it must be at least 8 characters. We'll also email it to the user if email is connected.</p>
                </>
              ) : (
                <div className="rounded-md border border-cc-green/30 bg-cc-green/10 p-4" data-testid="reset-result">
                  <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-1">New password</p>
                  <div className="flex items-center gap-2">
                    <code className="text-base font-mono font-semibold text-zinc-900 bg-white border rounded px-2 py-1 flex-1" data-testid="reset-new-password">{resetResult.password}</code>
                    <Button size="sm" variant="outline" onClick={copyPw} data-testid="copy-pw-btn">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</Button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-3">{resetResult.emailed ? `Emailed to ${resetResult.email}.` : "Email not sent (Microsoft 365 not connected) — share this password securely with the user."}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {!resetResult ? (
              <>
                <Button variant="outline" onClick={() => setResetting(null)}>Cancel</Button>
                <Button onClick={doReset} disabled={resetBusy} className="bg-cc-blue text-white" data-testid="confirm-reset-btn">{resetBusy ? "Resetting…" : (resetPw.trim() ? "Set password" : "Generate & reset")}</Button>
              </>
            ) : (
              <Button onClick={() => setResetting(null)} className="bg-cc-blue text-white" data-testid="reset-done-btn">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
